import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from '../api'
import type { FaultType, Language, ProcessStep } from '../types'

const fallbackFaults: Record<string, Record<Language, string>> = {
  engine: { zh: '发动机', en: 'Engine', pt: 'Motor' },
  transmission: { zh: '变速箱', en: 'Transmission', pt: 'Transmissão' },
  electrical: { zh: '电气系统', en: 'Electrical', pt: 'Elétrica' },
  ac: { zh: '空调系统', en: 'Air Conditioning', pt: 'Ar-condicionado' },
  brake: { zh: '制动系统', en: 'Brakes', pt: 'Freios' },
  suspension: { zh: '悬挂系统', en: 'Suspension', pt: 'Suspensão' },
  body: { zh: '车身', en: 'Body', pt: 'Carroceria' },
  other: { zh: '其他', en: 'Other', pt: 'Outros' },
}

const fallbackSteps: Record<string, Record<Language, string>> = {
  fault_report: { zh: '故障报修', en: 'Fault Report', pt: 'Relato de Falha' },
  initial_review: { zh: '初审', en: 'Initial Review', pt: 'Revisão Inicial' },
  technical_eval: { zh: '技术评估', en: 'Technical Evaluation', pt: 'Avaliação Técnica' },
  repair_execution: { zh: '维修执行', en: 'Repair Execution', pt: 'Execução do Reparo' },
  archived: { zh: '归档', en: 'Archived', pt: 'Arquivado' },
}

function localized(record: { label_zh?: string; label_en?: string; label_pt?: string }, language: string) {
  if (language === 'en') return record.label_en || record.label_zh || ''
  if (language === 'pt') return record.label_pt || record.label_zh || ''
  return record.label_zh || record.label_en || record.label_pt || ''
}

export const useConfigStore = defineStore('config', () => {
  const faultTypes = ref<FaultType[]>([])
  const processSteps = ref<ProcessStep[]>([])
  const loaded = ref(false)
  const loading = ref(false)
  let pending: Promise<void> | null = null

  const activeFaultTypes = computed(() => faultTypes.value.filter((item) => Boolean(item.is_active)))
  const activeProcessSteps = computed(() => processSteps.value.filter((item) => Boolean(item.is_active)))

  async function load(force = false) {
    if (loaded.value && !force) return
    if (pending) return pending
    loading.value = true
    pending = (async () => {
      const [faultResponse, processResponse] = await Promise.all([
        api.get<{ faultTypes: FaultType[] }>('/settings/fault-types'),
        api.get<{ steps: ProcessStep[] }>('/settings/process'),
      ])
      faultTypes.value = (faultResponse.faultTypes || []).map((item) => ({ ...item, is_active: Boolean(item.is_active) }))
      processSteps.value = (processResponse.steps || []).map((item) => ({ ...item, is_active: Boolean(item.is_active) }))
      loaded.value = true
    })()
    try {
      await pending
    } finally {
      pending = null
      loading.value = false
    }
  }

  function faultLabel(code: string, language: string) {
    const item = faultTypes.value.find((fault) => fault.code === code)
    return item ? localized(item, language) : fallbackFaults[code]?.[language as Language] || code
  }

  function stepLabel(code: string, language: string) {
    const item = processSteps.value.find((step) => step.step_name === code)
    if (item) {
      return localized({
        label_zh: item.step_label_zh,
        label_en: item.step_label_en,
        label_pt: item.step_label_pt,
      }, language)
    }
    return fallbackSteps[code]?.[language as Language] || code
  }

  function adjacentActive(code: string) {
    const index = processSteps.value.findIndex((step) => step.step_name === code)
    if (index < 0) return { previous: null, next: null }
    let previous: ProcessStep | null = null
    let next: ProcessStep | null = null
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      if (processSteps.value[cursor].is_active) {
        previous = processSteps.value[cursor]
        break
      }
    }
    for (let cursor = index + 1; cursor < processSteps.value.length; cursor += 1) {
      if (processSteps.value[cursor].is_active) {
        next = processSteps.value[cursor]
        break
      }
    }
    return { previous, next }
  }

  function reset() {
    faultTypes.value = []
    processSteps.value = []
    loaded.value = false
  }

  return {
    faultTypes,
    processSteps,
    activeFaultTypes,
    activeProcessSteps,
    loaded,
    loading,
    load,
    faultLabel,
    stepLabel,
    adjacentActive,
    reset,
  }
})
